import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { LogOut, Clock, MapPin, Camera, User, History } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DriverDashboard = () => {
  const navigate = useNavigate();
  const [driver, setDriver] = useState(null);
  const [activeShift, setActiveShift] = useState(null);
  const [showStartModal, setShowStartModal] = useState(false);
  const [showEndModal, setShowEndModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [startKm, setStartKm] = useState('');
  const [endKm, setEndKm] = useState('');
  const [startPhoto, setStartPhoto] = useState('');
  const [endPhoto, setEndPhoto] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/mobileapp/login'); return; }
    fetchDriverData();
    fetchActiveShift();
  }, []);

  const fetchDriverData = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/driver/me`, { headers: { Authorization: `Bearer ${token}` } });
      setDriver(res.data);
    } catch { toast.error('Failed to fetch driver data'); }
  };

  const fetchActiveShift = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/shift/active`, { headers: { Authorization: `Bearer ${token}` } });
      setActiveShift(res.data);
    } catch { /* no active shift */ }
  };

  const handlePhotoUpload = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => type === 'start' ? setStartPhoto(reader.result) : setEndPhoto(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleStartShift = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/shift/start`, { start_km: parseFloat(startKm), start_photo: startPhoto }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Shift started!');
      setActiveShift(res.data);
      setShowStartModal(false);
      setStartKm(''); setStartPhoto('');
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to start shift'); }
    finally { setLoading(false); }
  };

  const handleEndShift = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/shift/end`, { shift_id: activeShift.id, end_km: parseFloat(endKm), end_photo: endPhoto }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Shift ended!');
      setActiveShift(null);
      setShowEndModal(false);
      setEndKm(''); setEndPhoto('');
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to end shift'); }
    finally { setLoading(false); }
  };

  const handleLogout = () => { localStorage.removeItem('token'); localStorage.removeItem('user'); navigate('/mobileapp/login'); };

  if (!driver) return <div className="min-h-screen flex items-center justify-center"><div className="text-lg text-slate-600">Loading...</div></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10 backdrop-blur-md bg-white/80">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>PrimeSwift</h1>
            <p className="text-xs text-slate-500">{driver.name}</p>
          </div>
          <Button data-testid="logout-button" variant="ghost" size="sm" onClick={handleLogout}><LogOut className="w-4 h-4 mr-2" />Logout</Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {activeShift ? (
          <Card className="bg-gradient-to-br from-slate-900 to-slate-800 text-white border-0 shadow-lg" data-testid="active-shift-card">
            <CardHeader><CardTitle className="flex items-center space-x-2"><Clock className="w-5 h-5" /><span>Active Shift</span></CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between"><span className="text-slate-300">Start KM:</span><span className="text-xl font-bold">{activeShift.start_km}</span></div>
              <div className="flex items-center justify-between"><span className="text-slate-300">Start Time:</span><span className="font-medium">{new Date(activeShift.start_time).toLocaleString()}</span></div>
              <Button data-testid="end-shift-button" onClick={() => setShowEndModal(true)} className="w-full mt-4 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-full h-12">End Shift</Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-slate-100 shadow-sm" data-testid="no-active-shift-card">
            <CardHeader><CardTitle className="flex items-center space-x-2"><MapPin className="w-5 h-5 text-slate-400" /><span>No Active Shift</span></CardTitle></CardHeader>
            <CardContent>
              <p className="text-slate-600 mb-4">Ready to start your shift?</p>
              <Button data-testid="start-shift-button" onClick={() => setShowStartModal(true)} className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-full h-12">Start Shift</Button>
            </CardContent>
          </Card>
        )}

        <Card className="border-slate-100 shadow-sm">
          <CardContent className="pt-6">
            <Button data-testid="view-history-button" onClick={() => navigate('/mobileapp/history')} variant="outline" className="w-full border-slate-200 rounded-full h-12"><History className="w-4 h-4 mr-2" />View Trip History</Button>
          </CardContent>
        </Card>

        <Card className="border-slate-100 shadow-sm">
          <CardHeader><CardTitle className="flex items-center space-x-2"><User className="w-5 h-5" /><span>Driver Info</span></CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between"><span className="text-slate-600">Name:</span><span className="font-medium">{driver.name}</span></div>
            <div className="flex justify-between"><span className="text-slate-600">Mobile:</span><span className="font-medium">{driver.mobile}</span></div>
            <div className="flex justify-between"><span className="text-slate-600">License:</span><span className="font-medium">{driver.license_number}</span></div>
            <div className="flex justify-between"><span className="text-slate-600">Status:</span><span className="px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">{driver.status}</span></div>
          </CardContent>
        </Card>
      </div>

      <Dialog open={showStartModal} onOpenChange={setShowStartModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Start Shift</DialogTitle><DialogDescription>Enter starting odometer reading</DialogDescription></DialogHeader>
          <form onSubmit={handleStartShift} className="space-y-4">
            <div className="space-y-2"><Label>Starting KM</Label><Input data-testid="start-km-input" type="number" step="0.1" placeholder="Enter odometer" value={startKm} onChange={(e) => setStartKm(e.target.value)} className="h-12" required /></div>
            <div className="space-y-2"><Label>Odometer Photo</Label>
              <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 text-center">
                <input id="start-photo" data-testid="start-photo-input" type="file" accept="image/*" onChange={(e) => handlePhotoUpload(e, 'start')} className="hidden" />
                <label htmlFor="start-photo" className="cursor-pointer">{startPhoto ? <img src={startPhoto} alt="Start" className="max-h-32 mx-auto rounded" /> : <div><Camera className="w-8 h-8 mx-auto text-slate-400" /><p className="text-sm text-slate-600">Upload photo</p></div>}</label>
              </div>
            </div>
            <Button type="submit" data-testid="submit-start-shift" className="w-full bg-orange-500 hover:bg-orange-600 rounded-full h-12" disabled={loading}>{loading ? 'Starting...' : 'Start Shift'}</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showEndModal} onOpenChange={setShowEndModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>End Shift</DialogTitle><DialogDescription>Enter ending odometer reading</DialogDescription></DialogHeader>
          <form onSubmit={handleEndShift} className="space-y-4">
            <div className="space-y-2"><Label>Ending KM</Label><Input data-testid="end-km-input" type="number" step="0.1" placeholder="Enter odometer" value={endKm} onChange={(e) => setEndKm(e.target.value)} className="h-12" required /></div>
            <div className="space-y-2"><Label>Odometer Photo</Label>
              <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 text-center">
                <input id="end-photo" data-testid="end-photo-input" type="file" accept="image/*" onChange={(e) => handlePhotoUpload(e, 'end')} className="hidden" />
                <label htmlFor="end-photo" className="cursor-pointer">{endPhoto ? <img src={endPhoto} alt="End" className="max-h-32 mx-auto rounded" /> : <div><Camera className="w-8 h-8 mx-auto text-slate-400" /><p className="text-sm text-slate-600">Upload photo</p></div>}</label>
              </div>
            </div>
            <Button type="submit" data-testid="submit-end-shift" className="w-full bg-orange-500 hover:bg-orange-600 rounded-full h-12" disabled={loading}>{loading ? 'Ending...' : 'End Shift'}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DriverDashboard;
